import { newRelicPageAction } from "@jslib/shared/utils/logger";
import { getSafe } from "@jslib/shared/utils/getSafe";

import { type ProgramTheme } from "../types/types";

const IPQS_SCRIPT_ID = "ipqs";

const IPQS_SCRIPT_SRC =
  "https://fd.sheerid.com/api/*/BJOvvIiNpZnA9XHXIHVc0S4FO87k4eub6NLOfmShTU7nRqamLKTzQixwD7XETz7bvtNHmicHNx9hEtOJ9NPo3kUJBl7o1jpwcbcXeOMDJjvulAWSrRnO7WYq9gxL6xNT0xnfou5UlshUGWQ2g68qBuWajMWbxZ25JELntxaP0neiVUbephG5E79ES89qBo4uIGBDvykdJb75hpo0URvJ0Fm1j6fuEqHQBq64Mi390KC9XoQwiFxyboxQ5lSooY4p/learn.js";

const IPQS_SCRIPT_TIMEOUT_MS = 30000;

// How long to wait for IPQS to report a result after its script has loaded. Sized to
// cover their whole submission waterfall. Roughly 4s of setup then four attempts to
// unique endpoints at 10s each.
const IPQS_RESULT_TIMEOUT_MS = 45000;

const IPQS_PAGE_ACTION = "ipqs-collection";

type IpqsResult = { request_id?: string };

const loadIPQSDeviceFingerprintScript = (): Promise<void> => {
  if (document.getElementById(IPQS_SCRIPT_ID)) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const scriptTag = document.createElement("script");
    scriptTag.id = IPQS_SCRIPT_ID;
    scriptTag.async = true;

    let timeoutId = 0;

    const settle = (error?: Error): void => {
      window.clearTimeout(timeoutId);
      scriptTag.onload = null;
      scriptTag.onerror = null;

      if (error) {
        scriptTag.remove();
        reject(error);
      } else {
        resolve();
      }
    };

    timeoutId = window.setTimeout(
      () => settle(new Error(`IPQS script did not load within ${IPQS_SCRIPT_TIMEOUT_MS}ms`)),
      IPQS_SCRIPT_TIMEOUT_MS,
    );

    scriptTag.onload = () => settle();
    scriptTag.onerror = () => settle(new Error("IPQS script failed to load"));

    scriptTag.src = IPQS_SCRIPT_SRC;
    document.body.appendChild(scriptTag);
  });
};

let ipqsRequestId: string | undefined;

// Get IPQS's own identifier for the device lookup.
// Only set once collection succeeded so its presence means signals reached IPQS.
export const getIpqsRequestId = (): string | undefined => ipqsRequestId;

export const collectIPQSDeviceData = async (
  programTheme: ProgramTheme,
  verificationId: string,
): Promise<string | undefined> => {
  if (!getSafe(() => programTheme.ipqsDeviceFingerprintEnabled, false)) {
    return undefined;
  }

  const startedAt = Date.now();

  const reportFailure = (outcome: string, reason?: string): void =>
    newRelicPageAction(IPQS_PAGE_ACTION, {
      outcome,
      verificationId,
      elapsedMs: Date.now() - startedAt,
      ...(reason ? { reason } : {}),
    });

  // IPQS reports through callbacks, not the script load, so settle on whichever fires first.
  let reportResult: (requestId: string | undefined) => void;
  const collected = new Promise<string | undefined>((resolve) => {
    reportResult = resolve;
  });

  try {
    window.IPQ = {
      Callback: () => {
        window.Startup.Store("transactionID", verificationId);

        // AfterResult fires once IPQS has a result.
        window.Startup.AfterResult?.((result: IpqsResult) => {
          ipqsRequestId = result?.request_id;
          if (!ipqsRequestId) {
            reportFailure("noRequestId");
          }
          reportResult(ipqsRequestId);
        });
        // AfterFailure fires only after its internal retries are exhausted.
        window.Startup.AfterFailure?.(() => {
          reportFailure("failed");
          reportResult(undefined);
        });

        window.Startup.Init();
      },
    };

    await loadIPQSDeviceFingerprintScript();
  } catch (error) {
    reportFailure("scriptLoadFailed", error instanceof Error ? error.message : undefined);
    return undefined;
  }

  // Start this clock once the script is actually loaded, so a slow download doesn't
  // eat into the time IPQS needs to work through its retries.
  window.setTimeout(() => reportResult(undefined), IPQS_RESULT_TIMEOUT_MS);

  return collected;
};
